<template>
  <div class="rich-editor-container">
    <!-- 工具栏 -->
    <div class="toolbar" v-if="showToolbar">
      <button @click="insertImage">插入图片</button>
      <button @click="insertVideo">插入视频</button>
      <button @click="toggleFullscreen" class="fullscreen-btn">
        {{ fullscreen ? '退出全屏' : '全屏' }}
      </button>
    </div>

    <!-- 编辑器主体 -->
    <div class="editor-wrapper" :class="{ 'fullscreen': fullscreen }">
      <quill-editor
          v-model="content"
          ref="myQuillEditor"
          :options="editorOption"
          @change="onEditorChange"
      />
    </div>

    <!-- 底部状态栏 -->
    <div class="editor-footer">
      <span class="word-count">字数: {{ wordCount }}</span>
      <div>
        <button @click="cancelContent" class="save-btn mr-10">取消</button>
        <button @click="saveContent" class="save-btn">保存内容</button>
      </div>
    </div>

    <!-- 图片上传对话框 -->
    <div v-if="showImageDialog" class="upload-dialog">
      <div class="dialog-content">
        <h3>上传图片</h3>
        <input type="file" ref="imageInput" @change="handleImageUpload" accept="image/*">
        <div class="progress" v-if="uploadProgress > 0">
          <div class="progress-bar" :style="{ width: uploadProgress + '%' }"></div>
        </div>
        <div class="dialog-actions">
          <button @click="showImageDialog = false">取消</button>
          <button @click="confirmImageUpload" :disabled="!selectedImage">确认</button>
        </div>
      </div>
    </div>

    <!-- 视频插入对话框 -->
    <div v-if="showVideoDialog" class="upload-dialog">
      <div class="dialog-content">
        <h3>插入视频</h3>
        <div class="form-group">
          <label>视频URL:</label>
          <input type="text" v-model="videoUrl" placeholder="输入视频地址 (支持优酷、腾讯、YouTube等)">
        </div>
        <div class="form-group">
          <label>视频宽度:</label>
          <input type="number" v-model="videoWidth" placeholder="宽度(px)">
        </div>
        <div class="form-group">
          <label>视频高度:</label>
          <input type="number" v-model="videoHeight" placeholder="高度(px)">
        </div>
        <div class="dialog-actions">
          <button @click="showVideoDialog = false">取消</button>
          <button @click="insertVideoEmbed">插入</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {quillEditor} from 'vue-quill-editor'
import 'quill/dist/quill.core.css'
import 'quill/dist/quill.snow.css'
import 'quill/dist/quill.bubble.css'
import baseConfig from "@/utils/serverUrlConfig";
import local from "@/utils/localStroage";
import axios from "axios";
import imageURLDecrypt from "@/components/imageURLDecrypt";


// 自定义视频Blot
const VideoBlot = {
  // 定义Blot名称
  blotName: 'videoEmbed',
  // 定义标签名称
  tagName: 'div',
  // 定义类名
  className: 'ql-video-embed',

  // 创建DOM元素
  create: function(url) {
    let node = document.createElement(this.tagName)
    node.className = this.className
    node.setAttribute('contenteditable', 'false')

    // 解析不同平台的视频URL
    let embedUrl = this.parseVideoUrl(url)

    // 创建iframe
    let iframe = document.createElement('iframe')
    iframe.setAttribute('src', embedUrl)
    iframe.setAttribute('frameborder', '0')
    iframe.setAttribute('allowfullscreen', 'true')
    iframe.style.width = '100%'
    iframe.style.height = '100%'

    node.appendChild(iframe)
    return node
  },

  // 解析不同平台的视频URL
  parseVideoUrl: function(url) {
    // 其他直接返回
    return url
  },

  // 从DOM元素获取值
  value: function(node) {
    return node.querySelector('iframe').getAttribute('src')
  }
}

export default {
  components: {
    quillEditor
  },
  props: {
    initialContent: {
      type: String,
      default: ''
    },
    showToolbar: {
      type: Boolean,
      default: false
    },
    uploadUrl: {
      type: String,
      default: baseConfig.baseServerUrl + "/vid/uploadStaticBatch",
    }
  },
  data() {
    return {
      imgUrl: baseConfig.imgUrl,
      content: this.initialContent,
      fullscreen: false,
      showImageDialog: false,
      showVideoDialog: false,
      selectedImage: null,
      uploadProgress: 0,
      videoUrl: '',
      videoWidth: 640,
      videoHeight: 360,
      editorOption: {
        modules: {
          toolbar: {
            container: [
              ['bold', 'italic', 'underline', 'strike'],
              ['blockquote', 'code-block'],
              [{ 'header': 1 }, { 'header': 2 }],
              [{ 'list': 'ordered'}, { 'list': 'bullet' }],
              [{ 'script': 'sub'}, { 'script': 'super' }],
              [{ 'indent': '-1'}, { 'indent': '+1' }],
              [{ 'direction': 'rtl' }],
              [{ 'size': ['small', false, 'large', 'huge'] }],
              [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
              [{ 'color': [] }, { 'background': [] }],
              [{ 'font': [] }],
              [{ 'align': [] }],
              ['clean'],
              ['link', 'image', 'video'],
              ['fullscreen']  // 添加全屏按钮
            ],
            handlers: {
              'image': this.insertImage,
              'video': this.insertVideo
            }
          }
        },
        placeholder: '请输入内容...',
        theme: 'snow'
      }
    }
  },
  computed: {
    editor() {
      return this.$refs.myQuillEditor.quill
    },
    wordCount() {
      if (!this.content) return 0
      // 去除HTML标签后计算字数
      const text = this.content.replace(/<[^>]*>/g, '')
      return text.length
    }
  },
  methods: {
    // 注册自定义视频Blot
    registerVideoBlot() {
      const Quill = this.$refs.myQuillEditor.quill.constructor
      Quill.register(VideoBlot)
    },

    // 编辑器内容变化
    onEditorChange({ quill, html, text }) {
      this.content = html
      this.$emit('change', html,quill,text)
    },

    // 插入图片
    insertImage() {
      this.showImageDialog = true
      this.$nextTick(() => {
        this.$refs.imageInput.value = '' // 清除之前的文件选择
      })
    },

    // 处理图片上传
    handleImageUpload(e) {
      const file = e.target.files[0]
      if (!file) return

      if (!file.type.match('image.*')) {
        alert('请选择图片文件')
        return
      }

      if (file.size > 1 * 1024 * 1024) {
        alert('图片大小不能超过1MB')
        return
      }

      this.selectedImage = file
    },

    // 确认图片上传
    confirmImageUpload() {
      if (!this.selectedImage) return
      const formData = new FormData()
      formData.append('upload[]', this.selectedImage)
      // // 模拟上传过程
      this.uploadProgress = 0
      this.selectedImage = false;
      // 实际项目中使用axios上传
      axios.post(this.uploadUrl, formData, {
        onUploadProgress: (progressEvent) => {
          this.uploadProgress = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          )
        },
        header: {
          Authorization: local.get("userInfo").token,
        },
      }).then(response => {
        this.insertImageToEditor(response.data.data.filePath[0])
        this.showImageDialog = false
        this.selectedImage = null
        this.uploadProgress = 0
      }).catch(error => {
        window.console.error('上传失败:', error)
        this.uploadProgress = 0
      })
    },

    // 将图片插入编辑器
    async insertImageToEditor(imageUrl) {
      const range = this.editor.getSelection()
      if (range) {
        const tempURL = this.imgUrl + '/' + imageUrl;
        const imgDes = await imageURLDecrypt(tempURL);
        this.editor.insertEmbed(range.index, 'image', imgDes)
      }
    },

    // 插入视频
    insertVideo() {
      this.showVideoDialog = true
      this.videoUrl = ''
    },

    // 插入视频嵌入代码
    insertVideoEmbed() {
      if (!this.videoUrl) {
        alert('请输入视频URL')
        return
      }

      const range = this.editor.getSelection()
      if (range) {
        // 使用自定义的videoEmbed Blot
        this.editor.insertEmbed(range.index, 'videoEmbed', this.videoUrl)

        // 调整视频大小
        const videoElement = this.editor.getContents(range.index, 1).ops[0].insert.videoEmbed
        videoElement.style.width = `${this.videoWidth}px`
        videoElement.style.height = `${this.videoHeight}px`
      }

      this.showVideoDialog = false
    },

    // 切换全屏
    toggleFullscreen() {
      this.fullscreen = !this.fullscreen
      if (this.fullscreen) {
        document.body.style.overflow = 'hidden'
      } else {
        document.body.style.overflow = ''
      }
    },

    // 保存内容
    saveContent() {
      this.$emit('save', this.content)
    },

    // 保存内容
    cancelContent() {
      this.$emit('cancel')
    }
  },
  mounted() {
    // 注册自定义视频Blot
    this.registerVideoBlot()

    // 添加自定义CSS样式
    const style = document.createElement('style')
    style.textContent = `
      .ql-video-embed {
        position: relative;
        padding-bottom: 56.25%; /* 16:9 */
        height: 0;
        overflow: hidden;
        margin: 10px 0;
      }
      .ql-video-embed iframe {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
      }
    `
    document.head.appendChild(style)
  }
}
</script>

<style scoped>
.rich-editor-container {
  position: relative;
  border: 1px solid #ddd;
  border-radius: 4px;
  margin: 0px 0;
}

.toolbar {
  padding: 8px;
  background: #f5f5f5;
  border-bottom: 1px solid #ddd;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.toolbar button {
  padding: 4px 12px;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 3px;
  cursor: pointer;
}

.toolbar button:hover {
  background: #f0f0f0;
}

.editor-wrapper {
  height: 500px;
  overflow: hidden;
}

.editor-wrapper.fullscreen {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  height: auto;
  z-index: 1000;
  background: #fff;
}

.editor-footer {
  padding: 8px;
  background: #f5f5f5;
  border-top: 1px solid #ddd;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.save-btn {
  padding: 4px 12px;
  background: #42b983;
  color: white;
  border: none;
  border-radius: 3px;
  cursor: pointer;
}

.save-btn:hover {
  background: #3aa876;
}

.upload-dialog {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
}

.dialog-content {
  background: white;
  padding: 20px;
  border-radius: 5px;
  width: 500px;
  max-width: 90%;
}

.dialog-content h3 {
  margin-top: 0;
  margin-bottom: 20px;
}

.dialog-actions {
  margin-top: 20px;
  text-align: right;
}

.dialog-actions button {
  padding: 6px 12px;
  margin-left: 10px;
  cursor: pointer;
}

.progress {
  height: 6px;
  background: #eee;
  margin: 10px 0;
  border-radius: 3px;
  overflow: hidden;
}

.progress-bar {
  height: 100%;
  background: #42b983;
  transition: width 0.3s;
}

.form-group {
  margin-bottom: 15px;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
}

.form-group input {
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 3px;
}
.quill-editor{
  height: 100%!important;
}
</style>
